import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar, Cell,
} from "recharts";
import { Cpu, TrendingUp, DollarSign, Zap, Clock, Server, CheckCircle } from "lucide-react";

function generateUsageData() {
  const data = [];
  const now = Date.now();
  for (let i = 13; i >= 0; i--) {
    const date = new Date(now - i * 86400000);
    data.push({
      date: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      requests: Math.round(200 + Math.random() * 600),
      tokens: Math.round(40000 + Math.random() * 120000),
      savingsVsCommercial: +(Math.random() * 40 + 60).toFixed(1),
    });
  }
  return data;
}

const USAGE_DATA = generateUsageData();

const COMPARISON_DATA = [
  { model: "GPT-4o", cost: 2.50, latency: 380, label: "GPT-4o" },
  { model: "Claude Sonnet 4", cost: 3.00, latency: 450, label: "Claude S4" },
  { model: "Gemini 3 Flash", cost: 0.10, latency: 120, label: "Gem 3 Flash" },
  { model: "Gemma 4 27B", cost: 0.00, latency: 12, label: "Gemma 4" },
];

export default function Gemma4UsageWidget() {
  const totalRequests = useMemo(() => USAGE_DATA.reduce((s, d) => s + d.requests, 0), []);
  const totalTokens = useMemo(() => USAGE_DATA.reduce((s, d) => s + d.tokens, 0), []);
  const avgSavings = useMemo(() => (USAGE_DATA.reduce((s, d) => s + d.savingsVsCommercial, 0) / USAGE_DATA.length).toFixed(1), []);

  return (
    <div className="space-y-4" data-testid="gemma4-usage-widget">
      <div className="flex items-center gap-2">
        <Cpu className="w-5 h-5 text-amber-500" />
        <h2 className="text-lg font-outfit font-bold text-[var(--qn-foreground)]">Gemma 4 On-Prem Analytics</h2>
        <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-500">Self-Hosted</Badge>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "14d Requests", value: totalRequests.toLocaleString(), icon: Zap, color: "var(--qn-primary)" },
          { label: "Tokens Processed", value: (totalTokens / 1000).toFixed(0) + "K", icon: Server, color: "#8b5cf6" },
          { label: "Avg Savings vs Commercial", value: `${avgSavings}%`, icon: DollarSign, color: "#10b981" },
          { label: "Avg Latency", value: "12ms", icon: Clock, color: "#f59e0b" },
        ].map((stat) => (
          <div key={stat.label} className="p-3 rounded-lg bg-[var(--qn-surface)] border border-[var(--qn-border)] flex items-center gap-3">
            <div className="w-8 h-8 rounded flex items-center justify-center shrink-0" style={{ background: stat.color + "15" }}>
              <stat.icon className="w-4 h-4" style={{ color: stat.color }} />
            </div>
            <div>
              <div className="text-[10px] text-[var(--qn-foreground-muted)]">{stat.label}</div>
              <div className="text-sm font-bold font-mono text-[var(--qn-foreground)]">{stat.value}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Requests Over Time */}
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="text-sm font-outfit text-[var(--qn-foreground)]">Gemma 4 Request Volume (14d)</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={USAGE_DATA}>
                <defs>
                  <linearGradient id="gemmaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: "var(--qn-foreground-muted)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 9, fill: "var(--qn-foreground-muted)" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, fontSize: 11, color: "var(--qn-foreground)" }} wrapperStyle={{ outline: "none" }} />
                <Area type="monotone" dataKey="requests" stroke="#f59e0b" fill="url(#gemmaGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Cost Comparison */}
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="text-sm font-outfit text-[var(--qn-foreground)]">Cost per 1M Tokens vs Commercial</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={COMPARISON_DATA} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 9, fill: "var(--qn-foreground-muted)" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                <YAxis type="category" dataKey="label" tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} axisLine={false} tickLine={false} width={75} />
                <Tooltip contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, fontSize: 11, color: "var(--qn-foreground)" }} wrapperStyle={{ outline: "none" }} cursor={{ fill: "rgba(255,255,255,0.06)" }} formatter={(v) => [`$${v.toFixed(2)}/1M tokens`, ""]} />
                <Bar dataKey="cost" radius={[0, 4, 4, 0]}>
                  {COMPARISON_DATA.map((entry, i) => (
                    <Cell key={i} fill={entry.model === "Gemma 4 27B" ? "#10b981" : i === 0 ? "#3b82f6" : i === 1 ? "#8b5cf6" : "#06b6d4"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Deployment Details */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Deployment", items: [["Model", "gemma-4-27b-it"], ["Runtime", "vLLM v0.6.2"], ["GPU", "4x NVIDIA A100 80GB"]] },
          { label: "Performance", items: [["Throughput", "800 req/s"], ["P50 Latency", "12ms"], ["P99 Latency", "34ms"]] },
          { label: "Cost Savings", items: [["vs GPT-4o", "100% saved"], ["vs Claude S4", "100% saved"], ["vs Gemini Flash", "100% saved"]] },
        ].map((section) => (
          <div key={section.label} className="p-3 rounded-lg bg-[var(--qn-surface)] border border-[var(--qn-border)]">
            <div className="text-[10px] font-semibold text-[var(--qn-foreground-muted)] uppercase tracking-wider mb-2">{section.label}</div>
            <div className="space-y-1.5">
              {section.items.map(([k, v]) => (
                <div key={k} className="flex justify-between text-xs">
                  <span className="text-[var(--qn-foreground-muted)]">{k}</span>
                  <span className="font-mono text-[var(--qn-foreground)]">{v}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
